import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";

import { Cancellation } from "@/api";
import { useSingleton } from "@/composables";
import { EntitiesHelper, StoresHelper } from "@/helpers";
import { FavoriteDictionariesService } from "@/services";
import { useDictionariesStore } from "@/stores";

interface State {
  status: "initial" | "loading" | "loaded" | "failed";
  dictionaryIds: string[];
}

const getFavoriteDictionaryIdsSingleton = useSingleton(FavoriteDictionariesService.getDictionaryIds);

export const useFavoriteDictionariesStore = defineStore(
  "favoriteDictionaries",
  () => {
    const state = reactive<State>({
      status: "initial",
      dictionaryIds: []
    });

    const dictionariesStore = useDictionariesStore();

    const dictionaries = computed(() =>
      EntitiesHelper.filterByIds(dictionariesStore.dictionaries, state.dictionaryIds).filter(
        (dictionary) => !dictionariesStore.companyDictionaries.includes(dictionary)
      )
    );

    const fetch = async () => {
      const prevStatus = state.status;

      try {
        state.status = "loading";
        state.dictionaryIds = await getFavoriteDictionaryIdsSingleton();
        state.status = "loaded";
      } catch (e) {
        if (e instanceof Cancellation) {
          state.status = prevStatus;
          return;
        }

        state.status = "failed";
        throw e;
      }
    };

    const getPosition = (dictionaryId: string) => state.dictionaryIds.findIndex((id) => id === dictionaryId);

    const addDictionary = async (dictionaryId: string) => {
      try {
        state.dictionaryIds.push(dictionaryId);
        await FavoriteDictionariesService.addDictionary(dictionaryId);
      } catch (e) {
        state.dictionaryIds.splice(getPosition(dictionaryId), 1);
        throw e;
      }
    };

    const removeDictionary = async (dictionaryId: string) => {
      const position = getPosition(dictionaryId);

      try {
        state.dictionaryIds.splice(position, 1);
        await FavoriteDictionariesService.removeDictionary(dictionaryId);
      } catch (e) {
        state.dictionaryIds.splice(position, 0, dictionaryId);
        throw e;
      }
    };

    return {
      ...toRefs(state),
      dictionaries,
      fetch,
      addDictionary,
      removeDictionary
    };
  },
  {
    persist: {
      pick: StoresHelper.validatePaths<State>(["dictionaryIds"])
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFavoriteDictionariesStore, import.meta.hot));
}
